import { IMG } from "@/lib/images";
import { Chapter } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import ContentRow from "@/components/ContentRow";
import SectionCTA from "@/components/SectionCTA";
import { usePageMeta } from "@/lib/site";

const ROWS = [
  {
    title: "Home Additions & Expansions",
    copy: "When the home is right but the space isn't, an addition or expansion can change everything. We plan and build additions that feel original to the home — structurally sound, properly permitted, and finished with intent.",
    bullets: [
      "Bedrooms, bathrooms, offices, and recreational areas",
      "Second-story and rear expansions",
      "Structural, engineering, and permitting coordination",
      "Equitable Construction evaluation of the smartest way to expand",
    ],
    img: IMG.addition,
  },
  {
    title: "Garages & Garage Apartments",
    copy: "Detached and attached garages, garage build-outs, and finished living space above the garage. We handle the full path — evaluation, plans, engineering coordination, permits, and construction.",
    bullets: [
      "Detached and attached garages",
      "Living space above garages",
      "Workshop, storage, and flex space build-outs",
      "Utility, access, and site planning",
    ],
    img: IMG.concrete,
  },
  {
    title: "Mother-in-Law / ADU-Style Spaces",
    copy: "Private, functional living spaces for family, guests, or flexible use — designed around your property, your goals, and local requirements.",
    bullets: [
      "Detached, attached, and above-garage configurations",
      "Space planning for privacy and independence",
      "Zoning and permitting guidance",
      "Long-term flexibility built into the plan",
    ],
    img: IMG.renovation,
  },
  {
    title: "Multi-Family & Family Unit Properties",
    copy: "Duplexes, triplexes, fourplexes, and multi-generational family properties — planned and built with the same end-to-end guidance, from feasibility and permitting through construction and completion.",
    bullets: [
      "Duplex, triplex, and fourplex construction",
      "Multi-generational and family-unit layouts",
      "Zoning, permitting, and utility coordination",
      "Equitable Construction evaluation of the property's full potential",
    ],
    img: IMG.commercial,
  },
  {
    title: "Residential Renovations & Concrete",
    copy: "Strategic renovations that improve how your home functions — plus the concrete and exterior work that ties a property together: flatwork, driveways, foundations, and hardscaping.",
    bullets: [
      "Whole-home and targeted renovations",
      "Kitchen, bath, and living space updates",
      "Concrete flatwork, driveways, and foundations",
      "Exterior construction and site improvements",
    ],
    img: IMG.tradesperson,
  },
];

export default function Residential() {
  usePageMeta(
    "Residential Construction — Home Additions, Garages & ADUs",
    "Black Sky Builders builds home additions, garage apartments, ADU-style spaces, renovations, and concrete work across Salt Lake City and Utah."
  );

  return (
    <div data-testid="residential-page">
      <PageHero
        eyebrow="Residential Construction"
        lines={["Build Onto", "What You Love"]}
        copy="Home additions, expansions, garages, garage apartments, ADU-style spaces, multi-family and family unit properties, renovations, and concrete — planned and built by a team that manages the entire path from idea to completion."
        image={IMG.addition}
      />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24 lg:px-16">
        <Chapter
          index="01"
          eyebrow="Residential Services"
          title="Built around how you live"
          copy="Every residential project starts with your goals and your property — then we help develop the scope, navigate plans and permits, and manage the build."
        />
        {ROWS.map((row, i) => (
          <ContentRow key={row.title} index={i} flip={i % 2 === 1} {...row} />
        ))}
      </section>
      <SectionCTA
        title="Ready to expand your home?"
        copy="Tell us what you want to add, convert, or build — even if you're not sure where to start."
      />
    </div>
  );
}
